import { useGridCellEditor, type CustomCellEditorProps } from "ag-grid-react";
import { X } from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

export type SelectTableCellEditorParams = {
  options: string[];
  fieldKey: string;
  onCreateOption?: (args: {
    fieldKey: string;
    option: string;
  }) => Promise<boolean>;
};

function parseInitialValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((t) => String(t).trim()).filter(Boolean);
  }
  if (value != null && value !== "") {
    return [String(value).trim()].filter(Boolean);
  }
  return [];
}

export function SelectTableCellEditor(
  props: CustomCellEditorProps<unknown, string[]> & SelectTableCellEditorParams,
) {
  const {
    value,
    onValueChange,
    options: optionsProp,
    fieldKey,
    onCreateOption,
    stopEditing,
    eGridCell,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const isCreatingRef = useRef(false);
  const selected = useMemo(() => parseInitialValue(value), [value]);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<string[]>(optionsProp ?? []);
  const [isCreating, setIsCreating] = useState(false);

  useGridCellEditor({
    isPopup: () => true,
    getPopupPosition: () => "under",
    isCancelAfterEnd: () => isCreatingRef.current,
  });

  useEffect(() => {
    setOptions(optionsProp ?? []);
  }, [optionsProp]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  const trimmedQuery = query.trim();

  const canCreate = useMemo(() => {
    if (!trimmedQuery || isCreating || !onCreateOption) return false;
    const lower = trimmedQuery.toLowerCase();
    return !options.some((o) => o.toLowerCase() === lower);
  }, [isCreating, onCreateOption, options, trimmedQuery]);

  const removeTag = (tag: string) => {
    onValueChange(selected.filter((s) => s !== tag));
  };

  const toggleTag = (tag: string) => {
    const t = tag.trim();
    if (!t) return;
    if (selected.includes(t)) {
      removeTag(t);
    } else {
      onValueChange([...selected, t]);
    }
    setQuery("");
  };

  const createAndAdd = async (): Promise<boolean> => {
    if (!canCreate || !onCreateOption) return false;
    const option = trimmedQuery;
    isCreatingRef.current = true;
    setIsCreating(true);
    try {
      const ok = await onCreateOption({ fieldKey, option });
      if (!ok) return false;
      setOptions((prev) =>
        prev.some((o) => o.toLowerCase() === option.toLowerCase())
          ? prev
          : [...prev, option],
      );
      toggleTag(option);
      return true;
    } finally {
      isCreatingRef.current = false;
      setIsCreating(false);
    }
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      const lower = trimmedQuery.toLowerCase();
      const exactOption = options.find((o) => o.toLowerCase() === lower);
      if (exactOption) {
        toggleTag(exactOption);
        return;
      }
      if (canCreate) {
        void (async () => {
          const ok = await createAndAdd();
          if (ok) stopEditing?.();
        })();
        return;
      }
      if (!trimmedQuery) {
        stopEditing?.();
        return;
      }
      if (filtered.length > 0) {
        toggleTag(filtered[0] ?? "");
        return;
      }
      stopEditing?.();
      return;
    }
    if (event.key === "Backspace" && query === "" && selected.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      onValueChange(selected.slice(0, -1));
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      stopEditing?.(true);
    }
  };

  const showList = filtered.length > 0 || canCreate;
  const minWidth = Math.max(eGridCell?.offsetWidth ?? 0, 160);

  return (
    <div
      className="bg-background border-border z-[1] flex min-h-[30px] flex-col border text-sm shadow-lg"
      style={{ minWidth }}
    >
      <div className="flex flex-nowrap items-center gap-1 px-1 py-0 leading-none">
        {selected.map((tag) => (
          <span
            key={tag}
            className="inline-flex shrink-0 items-center gap-0.5 rounded-[2px] border border-[#5c5c5c] bg-[#3d3d3d] py-0 pr-0.5 pl-1 text-xs leading-none"
          >
            {tag}
            <button
              type="button"
              aria-label={`Remove ${tag}`}
              className="hover:bg-background/20 rounded-[1px] p-0.5"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => removeTag(tag)}
            >
              <X className="size-2.5" aria-hidden />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={query}
          disabled={isCreating}
          className="min-w-[4rem] flex-1 bg-transparent px-1 py-0 text-xs leading-none outline-none"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKeyDown}
        />
      </div>
      {showList ? (
        <ul className="border-border max-h-[220px] overflow-y-auto border-t py-0.5">
          {canCreate ? (
            <li>
              <button
                type="button"
                className="hover:bg-accent w-full px-2 py-1 text-left text-xs"
                disabled={isCreating}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void createAndAdd()}
              >
                Create &ldquo;{trimmedQuery}&rdquo;
              </button>
            </li>
          ) : null}
          {filtered.map((option) => {
            const isSelected = selected.includes(option);
            return (
              <li key={option}>
                <button
                  type="button"
                  className={`hover:bg-accent w-full px-2 py-1 text-left text-xs ${isSelected ? "bg-accent/50" : ""}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => toggleTag(option)}
                >
                  {option}
                  {isSelected ? " ✓" : ""}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
